/** @format */

import {
	afterRender,
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	NgZone,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Signal,
	signal,
	viewChild
} from '@angular/core';
import {
	AnalyticsService,
	DeviceService,
	FeatureConfigurationService,
	KillswitchService,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import { BreakPointNew, FeatureConfigurationKeys } from '@shared/data-access/enums';
import { COMPARE_PRODUCT_LIST_LIMIT } from '@compare/data-access';
import {
	Chain,
	CompareBarProduct,
	CustomBadgeFeatureParams,
	Environment,
	PlpInGridSponsoredProduct,
	ProductListSEO
} from '@shared/data-access/interfaces';
import { DEVICE_BREAKPOINT_MEDIA_QUERIES, IMPORTS, PROVIDERS } from './product-list-constants';
import {
	DisplayableProductListItem,
	FacetAccordionElement,
	FacetVO,
	PinnedContentSpot,
	ProductList,
	ProductVO
} from '@plp/data-access/interfaces';
import {
	GoogleAdManagerComponent,
	ProductRecommendationsComponent,
	SeoContentComponent
} from '@shared/components';
import { MLItem, MLItemType, MLStore } from '@machine-learning/data-access';
import {
	ProductListCompareService,
	ProductListMlService,
	ProductListSnackbarService
} from '@plp/data-access/services';
import {
	concatMap,
	filter,
	map,
	shareReplay,
	skip,
	skipWhile,
	take,
	throttleTime,
	withLatestFrom
} from 'rxjs/operators';
import {
	featureConfigCustomBadgesSet,
	productListContainerFeatureFlagsSet,
	refreshProductList,
	removeAllFilters,
	selectCompareBarProducts,
	selectCriteoProducts,
	selectCustomBadges,
	selectDisplayableProductListData,
	selectFeatureFlags,
	selectHasCatBubbles,
	selectIsAntiClearance,
	selectIsCompareFeatureHidden,
	selectIsProductListPageLoading,
	selectIsProductListPageValid,
	selectPageSize,
	selectProductList,
	selectProductListAvailability,
	selectProductListPromotion,
	selectProductListSEO,
	selectProductVOs,
	selectSelectedFacetsWithCount,
	selectSelectedFilters,
	selectSelectedSort,
	selectSkeletonLoader,
	selectValidFacetVOs
} from '@plp/data-access/state';
import { fromEvent, of } from 'rxjs';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { selectPageType, selectUserStoreInfo } from '@state/data-access';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { AuthenticationService } from '@shared/authentication/data-access';
import { CertonaService } from '@product-recommendations/data-access';
import { CriteoService } from '@shared/data-access/product-recommendations';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TransformPricesPipe } from '@plp/components';
import { WishListService } from '@shared/favorites/data-access/services';

@Component({
	selector: 'plp-srlp-product-list-container',
	standalone: true,
	templateUrl: './product-list-container.component.html',
	styleUrl: './product-list-container.component.scss',
	imports: [
		...IMPORTS,
		GoogleAdManagerComponent,
		ProductRecommendationsComponent,
		HmfButtonComponent,
		TransformPricesPipe,
		SeoContentComponent
	],
	providers: PROVIDERS,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListContainerComponent implements OnInit, OnDestroy, AfterViewInit {
	private readonly store = inject(Store);

	private readonly chain = inject(Chain);

	private readonly productContainerService = inject(CertonaService);

	private readonly destroyRef = inject(DestroyRef);

	private readonly utilityService = inject(UtilityService);

	private readonly changeRef = inject(ChangeDetectorRef);

	readonly killSwitchService = inject(KillswitchService);

	private readonly featureConfigurationService = inject(FeatureConfigurationService);

	private readonly router = inject(Router);

	private readonly deviceService = inject(DeviceService);

	private readonly plpMlService = inject(ProductListMlService);

	private readonly plpCompareService = inject(ProductListCompareService);

	private readonly plpSnackbarService = inject(ProductListSnackbarService);

	private readonly authService = inject(AuthenticationService);

	private readonly environment = inject(Environment);

	private readonly wishlistService = inject(WishListService);

	private readonly elementRef = inject(ElementRef);

	private readonly analyticsService = inject(AnalyticsService);

	private readonly criteoService = inject(CriteoService);

	private readonly window = inject<Window>(WINDOW);

	private readonly platformId = inject(PLATFORM_ID);

	private readonly ngZone = inject(NgZone);

	/**** START OF FEATURE FLAGS PROPERTIES ************************/
	readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

	readonly compareCheckboxEnabled = computed(() => this.featureFlags().compareCheckboxEnabled);

	readonly enableTooltipSurvey = computed(() => this.featureFlags().enableTooltipSurvey);

	readonly plpShowCustomerRating = computed(() => this.featureFlags().plpShowCustomerRating);

	readonly sameDayDeliveryEnabled = computed(() => this.featureFlags().sameDayDeliveryEnabled);

	readonly showNewSponsoredLabel = computed(() => this.featureFlags().showNewSponsoredLabel);

	readonly sortByCustomerRatingEnabled = computed(
		() => this.featureFlags().sortByCustomerRatingEnabled
	);

	readonly useImageSwatches = computed(() => this.featureFlags().useImageSwatches);

	readonly fulfillmentFacetsEnabled = computed(
		() => this.featureFlags().fulfillmentFacetsEnabled
	);

	readonly exposedFiltersEnabled = computed(() => this.featureFlags().exposedFiltersEnabled);

	readonly availabilityList$ = this.store.selectSignal(selectProductListAvailability);
	readonly isCompareFeatureHidden$ = this.store.selectSignal(selectIsCompareFeatureHidden);
	readonly customBadges$ = this.store.selectSignal(selectCustomBadges);
	readonly compareBarProducts$ = this.store.selectSignal(selectCompareBarProducts);
	readonly displayableProductListData$ = this.store
		.select(selectDisplayableProductListData)
		.pipe(shareReplay(1), takeUntilDestroyed(this.destroyRef));
	readonly dplData$ = this.displayableProductListData$.pipe(
		map((productListData) => ({ productListData }))
	);
	readonly isAntiClearance$ = this.store.selectSignal(selectIsAntiClearance);
	readonly isProductListPageLoading$ = this.store.selectSignal(selectIsProductListPageLoading);
	readonly isProductListPageValid$ = this.store.selectSignal(selectIsProductListPageValid);
	readonly pageType$ = this.store
		.select(selectPageType)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly criteoProducts$ = this.store
		.select(selectCriteoProducts)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly productListData$ = this.store
		.select(selectProductList)
		.pipe(takeUntilDestroyed(this.destroyRef));

	/*
		This is the list of the filters/facets tab in the left column
		it is used to maintain the status as the filter component goes through the whole lifecycle
		Example: { 'Brand': { open: true, showMore: false} }
		Means the Brand item in the accordion is open but with the complete list collapsed
	*/
	openFacets = signal<Record<string, { open: boolean; showMore: boolean }> | null>(null);

	readonly productListFacetVOs = this.store.selectSignal(selectValidFacetVOs);

	readonly hasCatBubbles = this.store.selectSignal(selectHasCatBubbles);

	readonly productListSEOData$ = this.store
		.select(selectProductListSEO)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly productVOs$ = this.store
		.select(selectProductVOs)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly promoList$ = this.store.selectSignal(selectProductListPromotion);
	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly selectedFacetsWithCount$ = this.store
		.select(selectSelectedFacetsWithCount)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly selectedSort$ = this.store.selectSignal(selectSelectedSort);
	readonly selectedStoreInfo$ = this.store.selectSignal(selectUserStoreInfo);
	readonly showSkeletonLoader$ = this.store
		.select(selectSkeletonLoader)
		.pipe(takeUntilDestroyed(this.destroyRef));
	readonly pageSizeStore$ = this.store
		.select(selectPageSize)
		.pipe(takeUntilDestroyed(this.destroyRef));

	readonly productListData = signal<ProductList | null>(null);
	// This is used to track the referrer for QuickView, based on the page type
	readonly productCardReferrer = signal<'plp' | 'srlp'>('plp');
	productInfoList: string[] = [];
	readonly chainAbbr = signal<string>('');
	readonly loaderDummyArray = signal<number[]>([]);
	readonly filtersOpen = signal<boolean>(false);
	readonly selectedFilterCount = signal<number>(0);
	readonly sortingByValidValues = computed(() =>
		this.isSortNumberValid(this.selectedSort$()?.value ?? 0)
	);

	readonly itemsPerRow = signal<number>(4);
	readonly isFulfillmentFiltersSelected = signal<boolean>(false);
	readonly isAuthenticated = signal<boolean>(false);
	readonly nikeConnectedProducts = signal<string[]>([]);
	currentFacets: {
		facet: string;
		values: {
			count: number;
			value: string;
		}[];
	}[] = [];
	private readonly displayableProductListData = toSignal(this.displayableProductListData$);
	readonly noProducts = computed(() => this.displayableProductListData().length === 0);
	readonly showAvailabilityData = computed(() => {
		const availabilityData = this.availabilityList$();
		const isAvailabilityEnabled = this.featureFlags().availabilityOnProductCardsEnabled;

		if (Array.isArray(availabilityData) && availabilityData.length === 0) {
			return false;
		}

		return isAvailabilityEnabled;
	});

	readonly dpld = toSignal<DisplayableProductListItem[]>(this.displayableProductListData$);
	readonly BreakPointSizeEnum = BreakPointNew;
	readonly MAX_NUMBER_ITEMS_PER_ROW = 4;
	readonly DEFAULT_IMAGE_PAGE_POSITION = 0;
	readonly IMAGE_PAGE_POSITION_5 = 5;
	/**
	 * @description
	 * To display mobile view on screens less than 1024px.
	 */
	readonly deviceBreakPoint = signal<BreakPointNew>(BreakPointNew.LARGE);

	/**
	 * @description
	 * Check If the certona response have scheme for PLA
	 */
	isResonanceReady = false;
	// Certona scheme name
	scheme2 = 'family1_rr';
	// Response for Certona Recommendations
	certonaRecommendationsResponse: any;

	// Check If the certona response have scheme for cart
	isCartCertona = false;
	readonly showNoSearchResultsPage = signal<boolean>(false);
	readonly pageType = signal<string>('plp');

	readonly currentFilterIndex = signal<number | null>(null);

	protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

	/**** Start of ML Integration properties ************************/
	private intersectionParams: IntersectionObserverInit = {
		// This is added to account for the space taken up by the product-list-header.
		// If the user is scrolling up, the impression will wait to fire until
		// the last portion of the image is out from under the header.
		root: null,
		rootMargin: '0%',
		threshold: 1 // 0.0 - 1.0 = 0% - 100%
	};
	private productCardObserver!: IntersectionObserver;
	private ecodesAlreadyReported: string[] = [];

	resetTheObserver = (): void => {
		this.productCardObserver = new IntersectionObserver(
			this.handleProductCardIntersection.bind(this),
			this.intersectionParams
		);
	};
	/**** End of ML Integration Properties ************************/

	readonly productListSEOData = signal<ProductListSEO | null>(null);
	readonly isStickySearchEnabled = signal<boolean>(false);
	readonly isFilterContainerSticky = signal<boolean>(false);
	readonly isPlp3Template = signal<boolean>(false);
	readonly compareListEcodes: Signal<string[]> = computed(() =>
		this.compareBarProducts$().map((cp) => cp.ecode)
	);
	isCompareAnalyticsTriggered = false;
	readonly skipLinkOpen = signal<boolean>(false);
	onViewGridItemBeacon = '';
	gridViewBeaconFired = false;
	productCardViewBeacons: string[] = [];
	lastVerticalScrollPosition = signal(window.scrollY);
	matchMediaListeners: MediaQueryList[] = [];

	/**
	 * Reference to the scrollable area element for shipping filters.
	 */
	readonly filtersShippingScrollableArea = viewChild<ElementRef<HTMLDivElement>>(
		'filtersShippingScrollableArea'
	);

	private readonly scrollState = signal({
		left: 0,
		scrollWidth: 0,
		clientWidth: 0
	});

	readonly showLeftArrow = computed(() => {
		const state = this.scrollState();
		return state.left > 0;
	});
	readonly showRightArrow = computed(() => {
		const state = this.scrollState();
		return state.left + state.clientWidth < state.scrollWidth;
	});

	private resizeObserver: ResizeObserver;
	private animationFrameId: number;

	constructor() {
		this.chainAbbr.set(this.chain.chainIdentifierAbbr);
		/*
		 * The callback is being bound to `this`
		 * in order to retain the context of the component
		 * within the function. Without it, the context of `this`
		 * changes to the IntersectionObserver class.
		 */
		if (isPlatformBrowser(this.platformId)) {
			this.productCardObserver = new IntersectionObserver(
				this.handleProductCardIntersection.bind(this),
				this.intersectionParams
			);

			//Scroll event for sticky filter container
			fromEvent(window, 'scroll')
				.pipe(throttleTime(100), takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.styleStickyFilterContainer();
				});

			// When the user hits the back/forward button, we are not correctly identifying queryparam changes and we are not refreshing
			// This is a hacky fix until we can properly fix back/forward button queryParam change refresh
			fromEvent(window, 'popstate')
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					window.location.reload();
				});

			// Custom document event - update feature flags
			fromEvent(document, 'WindowFlagsEvent')
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.killSwitchService
						.getAllKillSwitchesForCurrentChain$()
						.pipe(take(1))
						.subscribe((featureFlags: Record<string, boolean>) => {
							this.store.dispatch(
								productListContainerFeatureFlagsSet({ featureFlags })
							);
							this.changeRef.detectChanges();
						});
				});
		}
		afterRender(() => {
			this.updateScrollState();
			this.setupResizeObserver();
		});
	}

	ngOnInit(): void {
		this.featureConfigurationService.onFeature(
			FeatureConfigurationKeys.SET_PRODUCT_CUSTOM_BADGE,
			this.setCustomBadges.bind(this)
		);

		this.pageSizeStore$.subscribe((pageSizeElement) =>
			this.loaderDummyArray.set(new Array(pageSizeElement))
		);
		this.pageType$.subscribe((pageType) => this.pageType.set(pageType));
		this.getHomrBannerData();

		this.openFacets.set(this.getOpenFacets(this.productListFacetVOs()));
		this.updateFilterCountAndFilterQueryParam();
		this.setUpSelectorSubscriptions();
		this.setupCertonaRecommendations();
		this.windowResizeHandler();

		if (
			isPlatformBrowser(this.platformId) &&
			this.environment.thirdParty.auth0[this.chain.chainIdentifierAbbr].enabled
		) {
			this.getIsAuthenticated();
		}
	}

	ngAfterViewInit(): void {
		if (this.featureFlags().isPLPMLIntegrationEnabled && isPlatformBrowser(this.platformId)) {
			this.setItemsPerRow();
			/* This sets up the observers for the first page after all cards are fully loaded.
			 *  Otherwise, updates to the displayableProductListData calls this. */
			this.setupProductCardObservers();
		}
	}

	redirectToPLPOrPDP(landingPageURL: string): void {
		if (landingPageURL.toLowerCase().includes('www.')) {
			if (landingPageURL.toLowerCase().includes('http')) {
				window.location.assign(landingPageURL);
			} else {
				window.location.assign(`https://${landingPageURL}`);
			}
		} else if (!landingPageURL.startsWith('/')) {
			window.location.href = landingPageURL;
		} else if (window.location.hostname.includes('localhost')) {
			// For local dev purposes, include the port for redirects
			window.location.href = `${window.location.protocol}//${window.location.hostname}:${window.location.port}${landingPageURL}`;
		} else {
			window.location.href = `${window.location.protocol}//${window.location.hostname}${landingPageURL}`;
		}
	}

	setUpSelectorSubscriptions(): void {
		this.productListData$.subscribe((productList: ProductList | null) => {
			if (!productList) {
				return;
			}
			// If the landingPageURL is present, redirect to the PLP, /search/ -> /f/
			if (productList.searchVO.landingPageURL !== undefined) {
				this.redirectToPLPOrPDP(productList.searchVO.landingPageURL);
				return;
			}

			this.addSponsoredIntersectionObservers();
			this.productListData.set(productList);
			this.productCardReferrer.set(productList.searchVO.isFamilyPage ? 'plp' : 'srlp');
			this.showNoSearchResultsPage.set(
				productList?.productVOs &&
					productList.productVOs.length === 0 &&
					this.productListSEOData() === null &&
					this.selectedFilterCount() === 0
			);

			if (productList?.didYouMeanVO?.suggestions?.length) {
				this.router.navigate([], {
					queryParams: { searchTerm: productList.searchVO.searchTerm },
					queryParamsHandling: 'merge'
				});
			}
		});

		this.selectedFacetsWithCount$.subscribe((facets) => {
			this.currentFacets = facets;
		});
	}

	getHomrBannerData(): void {
		this.productListSEOData$.subscribe((response) => {
			this.productListSEOData.set(response);
			this.isPlp3Template.set(this.productListSEOData()?.attr?.LAYOUT_TYPE?.value === '8');
		});
	}

	addSponsoredIntersectionObservers(): void {
		if (
			!isPlatformBrowser(this.platformId) ||
			!this.utilityService.hasIntersectionObserverSupport()
		) {
			return;
		}

		if (this.featureFlags().isUsingCriteoSPAAdServer) {
			this.criteoProducts$
				.pipe(
					filter((products) => products?.placements[1]?.inGrid[0]?.products?.length > 0),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe((products) => {
					const allProducts = products?.placements[1]?.inGrid[0]?.products;
					if (!allProducts?.length) return;

					this.onViewGridItemBeacon = products.placements[1].inGrid[0].OnViewBeacon;

					// Poll for the elements to ensure they exist before attaching observers.
					// This prevents race conditions on slower networks/devices.
					const processedProductIds = new Set<string>();
					const maxRetries = 50; // Poll for a total of 5 seconds (50 * 100ms)
					let currentTry = 0;

					const pollForCriteoElements = () => {
						const unprocessedProducts = allProducts.filter(
							(p) => !processedProductIds.has(p.ProductId)
						);

						for (const item of unprocessedProducts) {
							const trackingId = `criteo-${item.ProductId}`;
							const sponsoredProductCard = document.getElementsByClassName(
								trackingId
							)[0] as HTMLElement;

							if (sponsoredProductCard) {
								if (!this.productCardViewBeacons.includes(item.OnViewBeacon)) {
									const criteoCallback = () => {
										this.criteoService.fireBeacon(item.OnViewBeacon);
									};
									this.utilityService.singleViewIntersectionObserver(
										sponsoredProductCard,
										criteoCallback,
										0.5
									);
									this.productCardViewBeacons.push(item.OnViewBeacon);
								}
								processedProductIds.add(item.ProductId);
							}
						}

						// If there are still unprocessed products and we haven't reached the max retries, poll again.
						if (
							processedProductIds.size < allProducts.length &&
							currentTry < maxRetries
						) {
							currentTry++;
							setTimeout(pollForCriteoElements, 100);
						}
					};

					pollForCriteoElements();
					this.gridViewBeaconFired = false;
				});
		}
	}

	async removeFilters(): Promise<void> {
		await this.utilityService.yieldTask(() => {
			this.store.dispatch(removeAllFilters());
			this.store.dispatch(refreshProductList());
		});
	}

	isSortNumberValid(selectedSortNum: number): boolean {
		return !(selectedSortNum === 2 || selectedSortNum === 3 || selectedSortNum === 4);
	}

	setupProductCardObservers(): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		this.displayableProductListData$
			.pipe(
				skip(1),
				filter((dpld) => !!dpld?.length),
				withLatestFrom(this.productListData$, this.showSkeletonLoader$),
				skipWhile(
					([dpld, productList, listIsLoading]) =>
						listIsLoading || !dpld.length || !productList
				)
			)
			.subscribe(([dpld]) => {
				// Clear the list of ecodes that have been reported to the ML API
				this.ecodesAlreadyReported = [];
				this.productCardObserver.disconnect();
				this.resetTheObserver();
				const productVOs = dpld.filter(
					(product) => (product as ProductVO).parentPartnumber
				);
				this.setNikeConnectedProducts(productVOs as ProductVO[]);

				// This is used to track the ecodes of the products that are displayed on the page.
				// parentPartnumber -> regular products, product_id -> sponsored products
				this.productInfoList = dpld.map(
					(item: DisplayableProductListItem): string =>
						(item as ProductVO).parentPartnumber ??
						(item as PlpInGridSponsoredProduct).product_id ??
						`${(item as PinnedContentSpot).csid}-${(item as PinnedContentSpot).pinnedPosition}`
				);

				this.changeRef.detectChanges();

				dpld.forEach((x, index) => {
					const pcimg: Element | null =
						this.elementRef.nativeElement.querySelector(
							`#plp-container #product-card-${index} .product-card-image`
						) ??
						this.elementRef.nativeElement.querySelector(
							`#plp-container #sponsored-product-${index} .product-card-image`
						) ??
						this.elementRef.nativeElement.querySelector(
							`#plp-container #pinned-content-${index} .pinned-content-intersection-observation-block`
						);

					if (pcimg) {
						this.productCardObserver.observe(pcimg);
					}
				});
			});
	}

	recordClickEvent(
		item: { event: PointerEvent; pdpUrl: string },
		product: ProductVO,
		index: number
	): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		// This is used here to prevent the product-card's <a> tag from navigating to the PDP before the Click Event is sent.
		// Navigation is handled below in the complete() callback.
		item.event.preventDefault();
		// catgroupId is only utilized on family pages, not search. If -1 is detected, an empty string is ultimately sent in the API payload.
		const catgroupId: number | null = this.productListSEOData()?.catgroupId ?? null;
		this.plpMlService.sendClickEventData(
			MLStore[this.chainAbbr().toUpperCase()],
			product,
			this.dpld(),
			this.productListData(),
			0,
			catgroupId,
			index
		);
		this.window.location.href = item.pdpUrl;
	}

	handleCompareSelection(event: { compareData: CompareBarProduct; isChecked: boolean }): void {
		if (event.isChecked && this.compareListEcodes().length >= COMPARE_PRODUCT_LIST_LIMIT) {
			this.plpSnackbarService.openSnackbar(
				'Only 5 products can be added to compare. Please remove a product before making another selection.'
			);

			return;
		}

		if (event.isChecked) {
			this.utilityService.deferOnPrerendering(() => this.compareCheckboxUsedAnalytics());
			const compareSource = this.productListSEOData()?.id
				? `PLP: ${this.productListSEOData().id}`
				: `Search: ${this.productListData()?.searchVO?.searchTerm}`;
			event.compareData.compareSource = compareSource;
			event.compareData.gridImageURL = this.plpCompareService.modifyImgSrcWidthAndHeight(
				event.compareData.imgURL
			);

			this.plpCompareService.addProductToCompareList(event.compareData);
		} else {
			this.plpCompareService.removeProductFromCompareList(event.compareData.ecode);
		}
	}

	//To be removed once the compare feature goes to 100% in produciton.
	private compareCheckboxUsedAnalytics(): void {
		//Trigger analytics on compare checkbox click only once per page view
		if (!this.isCompareAnalyticsTriggered) {
			this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
				event: 'compareCheckboxUsed'
			});
			this.isCompareAnalyticsTriggered = true;
		}
	}

	handleTabPressed() {
		this.skipLinkOpen.set(true);
	}

	private setCustomBadges(customBadgeParams: CustomBadgeFeatureParams): void {
		const customBadges = {};
		const { ecodes, text, textColor, background } = customBadgeParams;

		if (ecodes && text && textColor && background) {
			ecodes.forEach((ecode) => {
				customBadges[ecode] = {
					text,
					textColor,
					background
				};
			});

			this.store.dispatch(featureConfigCustomBadgesSet({ customBadges }));
		}
	}

	/**
	 * Styles the sticky filter container based on certain conditions.
	 */
	private styleStickyFilterContainer(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const isScrollingUp = window.scrollY < this.lastVerticalScrollPosition();
		const stickySearchBar = document.querySelector('.dsg-header-search-sticky-enabled.sticky');

		this.isStickySearchEnabled.set(!!stickySearchBar && isScrollingUp);

		const stickyFilterContainer: Element | null =
			this.deviceBreakPoint() <= 3
				? this.elementRef.nativeElement.querySelector('#sticky-filter-container-mobile')
				: this.elementRef.nativeElement.querySelector('#sticky-filter-container-desktop');

		this.lastVerticalScrollPosition.set(window.scrollY);
		// Edge Cases: Color Breakout Page, No Search Results - no filter is present
		if (!stickyFilterContainer) {
			return;
		}

		const stickyFilterBottom = stickyFilterContainer.getBoundingClientRect().bottom;

		const filterTop = parseInt(window.getComputedStyle(stickyFilterContainer).top);

		const compareBar =
			this.elementRef.nativeElement.querySelector('#compare-bar-mobile') ??
			this.elementRef.nativeElement.querySelector('#compare-bar-desktop');

		const fulfilmentHeightForMobile =
			this.deviceBreakPoint() <= 3 && this.isFulfillmentFiltersSelected()
				? this.elementRef.nativeElement.querySelector('.sticky-filter-chip').clientHeight
				: 0;

		// Calculate exposed filters height consistently, regardless of current sticky state
		const exposedFiltersElement = this.elementRef.nativeElement.querySelector(
			'.exposed-filter-sort-mobile'
		);
		const exposedFiltersHeight = exposedFiltersElement?.clientHeight ?? 0;

		// 112 = 32px (top spacing when sticky) + 80px (height of product-list-header)
		// When the bottom of the sticky filter container is less than or equal to the top of the product-list-header plus some spacing,
		// make the filter container non-sticky to prevent overlapping.
		const headerContainerHeight = 112;

		if (compareBar) {
			const compareBarHeight = parseInt(window.getComputedStyle(compareBar).height);
			this.isFilterContainerSticky.set(
				stickyFilterBottom <=
					headerContainerHeight +
						filterTop +
						compareBarHeight +
						fulfilmentHeightForMobile +
						exposedFiltersHeight
			);
		} else {
			this.isFilterContainerSticky.set(
				stickyFilterBottom <=
					headerContainerHeight +
						filterTop +
						fulfilmentHeightForMobile +
						exposedFiltersHeight
			);
		}
	}

	private getIsAuthenticated(): void {
		this.authService
			.getIsAuthenticated$()
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				concatMap((authResponse: boolean) => {
					this.isAuthenticated.set(authResponse);
					if (!this.isAuthenticated()) {
						return of([]);
					}
					this.window.addEventListener('updateFavoritesStoreData', () => {
						this.updateFavoritesFromQuickview();
					});

					return this.wishlistService.getFavoritedEcodes();
				})
			)
			.subscribe();
	}

	private updateFavoritesFromQuickview() {
		this.wishlistService.getFavoritedEcodes().pipe(take(1)).subscribe();
	}

	private setupCertonaRecommendations(): void {
		// Check to see if a Certona callback has been defined already
		if (typeof this.window.certonaRecommendations === 'undefined') {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.certonaRecommendations = this.getCertonaRecommendations.bind(this);
		}

		// Check to see if a Certona callback has been defined already
		if (
			typeof this.window.DSGShoppingPathJS === 'undefined' ||
			typeof this.window.DSGShoppingPathJS.certonaRecommendations === 'undefined'
		) {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.DSGShoppingPathJS = {
				certonaRecommendations: this.getCertonaRecommendations.bind(this)
			};
		}
	}

	private getCertonaRecommendations(certonaResponse: any): void {
		// Check to see if there is a response from Certona
		if (typeof certonaResponse !== 'undefined') {
			if (typeof this.certonaRecommendationsResponse === 'undefined') {
				// Set the response on the component
				this.certonaRecommendationsResponse = certonaResponse;
			} else if (
				this.certonaRecommendationsResponse?.resonance?.schemes &&
				certonaResponse?.resonance?.schemes
			) {
				const mergedCertonaSchemes = [
					...this.certonaRecommendationsResponse.resonance.schemes,
					...certonaResponse.resonance.schemes
				];

				const uniqueCertonaSchemes = [];

				// Map for testing uniqueness
				const schemeMap = new Map();

				// Iterate the merged schemes
				for (const item of mergedCertonaSchemes) {
					// Check to see if the scheme exists already
					if (!schemeMap.has(item.scheme)) {
						// set any value to Map
						schemeMap.set(item.scheme, true);
						// Add the scheme to the result Array
						uniqueCertonaSchemes.push({ ...item });
					}
				}

				// Set the response on the component by merging the new with the existing
				this.certonaRecommendationsResponse = {
					resonance: {
						schemes: uniqueCertonaSchemes
					}
				};
			}
		}
		this.changeRef.detectChanges();
		this.productContainerService.recommendations(this.certonaRecommendationsResponse);
	}

	/**
	 * @description
	 * When Selected Filters changes, update the filter count and update the query params
	 */
	private updateFilterCountAndFilterQueryParam(): void {
		this.selectedFilters$.subscribe((filters) => {
			let count = 0;
			// Loop through different selected filter types to retrieve the total count of filters selected
			Object.keys(filters).forEach((prop) => {
				count += filters[prop].length;
			});
			this.selectedFilterCount.set(count);

			// For page onload
			this.isFulfillmentFiltersSelected.set(!!filters['facetStore']?.length);
		});
	}

	/**
	 * Starting from the target element, recursively step up to the next parent element
	 * that contains the id number found in `product-card-`, `sponsored-product-`, or `pinned-content-`
	 * used to obtain the list of ecodes in each respective product row.
	 */
	private getProductCardMetadata(
		el: Element | HTMLElement | null
	): { index: number; isPinnedProduct: boolean; type: string } | undefined {
		const parentCardIdMatch: RegExpMatchArray | null =
			/^(product-card-|sponsored-product-|pinned-content-)\d+$/.exec(el?.id);

		if (parentCardIdMatch && el?.id) {
			// "product-card-5" => {index: 5, isPinnedProduct: false, type: "product-card"}
			const isPinnedProduct: boolean = el.getAttribute('data-em')?.includes('_pinned');
			const index = parseInt(el.id.split('-')[2], 10);
			const type = parentCardIdMatch[1].slice(0, -1); // use the regex match term and trim off the '-' at the end => "product-card"
			return { index, isPinnedProduct, type };
		} else if (el?.parentElement) {
			return this.getProductCardMetadata(el.parentElement);
		} else {
			return undefined;
		}
	}

	private windowResizeHandler(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		Object.keys(DEVICE_BREAKPOINT_MEDIA_QUERIES).forEach((key) => {
			const mediaQuery = window.matchMedia(DEVICE_BREAKPOINT_MEDIA_QUERIES[key]);
			mediaQuery.onchange = () => {
				this.filteredMatchMediaCalls(mediaQuery);
			};
			this.matchMediaListeners.push(mediaQuery);
		});
	}

	private filteredMatchMediaCalls(mediaQuery: MediaQueryList): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		if (mediaQuery.matches) {
			this.setItemsPerRow(true);
		}
	}

	private setItemsPerRow(forceCalculate = false): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		this.deviceBreakPoint.set(this.deviceService.getDeviceBreakPointNew(forceCalculate));

		switch (this.deviceBreakPoint()) {
			case BreakPointNew.EXTRAEXTRASMALL:
				this.itemsPerRow.set(1);
				break;
			case BreakPointNew.EXTRASMALL:
			case BreakPointNew.SMALL:
				this.itemsPerRow.set(2);
				break;
			case BreakPointNew.MEDIUM:
			case BreakPointNew.LARGE:
			case BreakPointNew.EXTRALARGE:
				this.itemsPerRow.set(3);
				break;
			case BreakPointNew.EXTRAEXTRALARGE:
				this.itemsPerRow.set(4);
				break;
		}
	}

	// This is the callback used by the IntersectionObserver to handle when each new row of items enters the viewport.
	private handleProductCardIntersection(entries: IntersectionObserverEntry[]): void {
		if (!this.featureFlags().isPLPMLIntegrationEnabled || !isPlatformBrowser(this.platformId)) {
			return;
		}
		const itemGroup: MLItem[] = [];
		const intersectingCards = entries.filter((entry) => entry.isIntersecting);
		if (!intersectingCards.length) {
			return;
		}

		for (const item of intersectingCards) {
			const card: { index: number; isPinnedProduct: boolean; type: string } | undefined =
				this.getProductCardMetadata(item.target);

			if (card === undefined) {
				return;
			}

			let type = MLItemType.PRODUCT;
			if (card.isPinnedProduct) {
				type = MLItemType.PINNED_PRODUCT;
			} else if (card.type === 'sponsored-product') {
				type = MLItemType.SPONSORED_PRODUCT;
				if (this.featureFlags().isUsingCriteoSPAAdServer && !this.gridViewBeaconFired) {
					this.criteoService.fireBeacon(this.onViewGridItemBeacon);
					this.gridViewBeaconFired = true;
				}
			} else if (card.type === 'pinned-content') {
				type = MLItemType.PINNED_CONTENT_SPOT;
			}

			const ecode: string = this.productInfoList[card.index];
			if (this.ecodesAlreadyReported.includes(ecode)) {
				continue;
			}
			this.ecodesAlreadyReported.push(ecode);

			const nextItem: MLItem = {
				id: ecode,
				num: card.index + 1, // 1-based index
				type
			};
			itemGroup.push(nextItem);

			// Once the intersection event has fired on an item, stop tracking its intersections.
			this.productCardObserver.unobserve(item.target);
		}

		this.plpMlService.sendImpressionEventData(
			itemGroup,
			this.currentFacets,
			this.chainAbbr(),
			this.productListData()?.facetVOs ?? [],
			this.productListData()?.totalCount ?? 0
		);
	}

	/**
	 * Determines and returns the state of facets (open or closed) based on the provided facets array.
	 * The first 4 facets are open by default, while the rest are closed. If it's plp3 template, only the first facet, category, will be open.
	 *
	 * @param facets - An array of `FacetVO` objects representing the facets to be processed.
	 * @returns A record where each key is a facet attribute name and the value is an object
	 * containing the `open` and `showMore` states.
	 */
	getOpenFacets(facets: FacetVO[]): Record<string, { open: boolean; showMore: boolean }> {
		const openFacets = {};

		facets.forEach((facet, index) => {
			let numberOfOpenFacets = 3;
			// Number of open facets for superfamily and plp3 template
			if (this.hasCatBubbles() && this.productListSEOData() !== null) {
				numberOfOpenFacets = this.isPlp3Template() ? 0 : 4;
			}

			if (index <= numberOfOpenFacets) {
				openFacets[facet.attrName] = { open: true, showMore: false };
			} else {
				openFacets[facet.attrName] = { open: false, showMore: false };
			}
		});

		return openFacets;
	}

	private setNikeConnectedProducts(displayableProducts: ProductVO[]) {
		let isNikeConnected = false;
		displayableProducts.forEach((product) => {
			if (product.attributes) {
				isNikeConnected = !!JSON.parse(product.attributes).find(
					(attribute) => attribute['5632'] && attribute['5632'] === 'Nike APE Exclusive'
				);
			}

			if (isNikeConnected && product.parentPartnumber) {
				this.nikeConnectedProducts.set([
					...this.nikeConnectedProducts(),
					product.parentPartnumber
				]);
				isNikeConnected = false;
			}
		});
	}

	onFacetToggled({ attrName, open, showMore }: FacetAccordionElement) {
		const facets = this.openFacets();
		if (facets) {
			facets[attrName] = { open, showMore };
			this.openFacets.set({ ...facets });
		}
	}

	setCurrentFilterIndex(index: number): void {
		this.currentFilterIndex.set(index);
	}

	focusFirstAccordion(): void {
		setTimeout(() => {
			this.currentFilterIndex.set(0);
			this.accordionFocusHandler(this.currentFilterIndex());
		}, 100);
	}

	arrowDownKeyHandler(event: KeyboardEvent): void {
		const facetVosLength = this.productListData()?.facetVOs?.length; //number of accordion components in filter column

		if (!facetVosLength) {
			return;
		}

		event.preventDefault();
		const arrowDownFilterIndex =
			this.currentFilterIndex() === null
				? 0
				: (this.currentFilterIndex() + 1) % facetVosLength;

		this.currentFilterIndex.set(arrowDownFilterIndex);
		this.accordionFocusHandler(this.currentFilterIndex());
	}

	arrowUpKeyHandler(event: KeyboardEvent): void {
		const facetVosLength = this.productListData()?.facetVOs?.length; //number of accordion components in filter column

		if (!facetVosLength) {
			return;
		}

		event.preventDefault();
		let arrowUpFilterIndex =
			this.currentFilterIndex() === null
				? this.currentFilterIndex() - 1
				: (this.currentFilterIndex() - 1 + facetVosLength) % facetVosLength;

		// Navigating through the checkboxes, a category item, size option, brand search input or the "view more" link does not change the accordion we are in.
		// This makes sure we return to the current accordion header, and not the skipping one above.
		// if for some reason your are going to change classes in filter.component.html make sure to update this
		const filterOptionsClasses = [
			'filter-value-count-container',
			'homefield-checkbox-input',
			'homefield-text-link',
			'homefield-actiontext',
			'size-filter'
		];

		const wasKeyPressedInAFilterOption = filterOptionsClasses.some((className) =>
			(event.target as HTMLElement).classList?.contains(className)
		);
		const wasKeyPressedFromBrandSearchInput = (event.target as HTMLElement)
			.getAttribute('id')
			?.includes('homefield-textinput');

		if (wasKeyPressedInAFilterOption || wasKeyPressedFromBrandSearchInput) {
			arrowUpFilterIndex = this.currentFilterIndex();
		}

		this.currentFilterIndex.set(arrowUpFilterIndex);
		this.accordionFocusHandler(this.currentFilterIndex());
	}

	tabKeyHandler(index: number): void {
		this.currentFilterIndex.set(index);
	}

	private accordionFocusHandler(facetIndex: number): void {
		const accordionHeaders =
			this.deviceBreakPoint() <= 3
				? document.querySelectorAll('.filter-container .accordion-header')
				: document.querySelectorAll('.plp-srlp-filters .accordion-header');
		if (accordionHeaders[facetIndex]) {
			(accordionHeaders[facetIndex] as HTMLElement).focus();
		}
	}

	protected trackFilterName(index: number, facetVO: FacetVO): string {
		return index.toString() + facetVO.attrName;
	}

	protected trackDPLD(index: number, item: DisplayableProductListItem): string {
		// Optional combo of properties from each type of card
		// Normal = name + parentPartnumber, criteo = product_id, Pinned Content = headline
		let value = index.toString();
		if ('name' in item) {
			value += `-${item.name}-${item?.parentPartnumber}`;
		}
		if ('product_id' in item) {
			value += `-${item.product_id}`;
		}
		if ('headline' in item) {
			value += `-${item.headline}`;
		}
		return value;
	}

	onSkipLinkEnter(currentTarget: KeyboardEvent) {
		// Prevent the click event from being triggered on the "homefield-color-swatch" component when the button is focused.
		currentTarget.preventDefault();

		this.skipLinkOpen.set(false);
		const productCardContent: HTMLDivElement | null = document.querySelector(
			'#plp-container .product-card .swatches-container button'
		);
		if (productCardContent) {
			productCardContent.focus();
		}
	}

	ngOnDestroy(): void {
		this.matchMediaListeners.forEach((mediaQuery) => {
			mediaQuery.onchange = null;
		});
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
		}
	}

	/**
	 * Scrolls the filters container horizontally by 30% of its visible width in the specified direction.
	 *
	 * This method calculates the scroll step based on the container's width and scrolls either left or right.
	 * It ensures the scroll position stays within valid bounds and updates the scroll state accordingly.
	 * After scrolling, it triggers an update to the scroll state after a short delay.
	 */
	scrollFilters(direction: 'left' | 'right'): void {
		const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
		if (!filtersContainerElem) return;

		//Scroll by 30% of the container's width
		const scrollStep = Math.floor(filtersContainerElem.clientWidth * 0.3);
		const scrollAmount = direction === 'right' ? scrollStep : -scrollStep;
		const maxScroll = filtersContainerElem.scrollWidth - filtersContainerElem.clientWidth;
		const newScrollPosition = filtersContainerElem.scrollLeft + scrollAmount;
		const targetScrollPosition = Math.min(Math.max(newScrollPosition, 0), maxScroll);
		this.scrollState.set({
			left: filtersContainerElem.scrollLeft,
			scrollWidth: filtersContainerElem.scrollWidth,
			clientWidth: filtersContainerElem.clientWidth
		});
		filtersContainerElem.scrollTo({ left: targetScrollPosition, behavior: 'smooth' });
	}

	private updateScrollState(): void {
		const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
		if (!filtersContainerElem) return;

		this.scrollState.set({
			left: filtersContainerElem.scrollLeft,
			scrollWidth: filtersContainerElem.scrollWidth,
			clientWidth: filtersContainerElem.clientWidth
		});
	}

	//Setup resize observer for scroll state updates
	private setupResizeObserver(): void {
		if (isPlatformServer(this.platformId)) return;

		const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
		if (!filtersContainerElem) return;

		this.resizeObserver = new ResizeObserver(() => {
			this.updateScrollState();
		});

		this.resizeObserver.observe(filtersContainerElem);
	}

	onFilterScroll(): void {
		if (!this.featureFlags().exposedFiltersEnabled) return;

		// Used requestAnimationFrame to limit the number of times updateScrollState is called during scrolling
		// This improves performance by batching updates to the next animation frame
		// Run outside Angular to avoid triggering change detection on every scroll event
		this.ngZone.runOutsideAngular(() => {
			// Handle scroll event
			this.animationFrameId = requestAnimationFrame(() => {
				this.updateScrollState();
			});
		});
	}
}
